/**
 * Studio (§31) — the ONE chrome every super-admin tool wears.
 *
 * Seven tools ship five different headers in classic (App Stats' ghost "Back to
 * Settings" + tab pills, Email Flows' slate-800 bar with a divider, the KB's
 * purple gradient tile with two coloured tab underlines, Announcements'
 * PartyPopper, Workshops' rose GraduationCap, Health's plain Back + black
 * Refresh). The mock collapses all five into one row — back · neutral 28 glyph ·
 * 17 title with a 13 sub · centred search where the tool searches · right
 * cluster · a 44px tab row under the 64 header, never inside it.
 *
 * Mock: .design-shots/redesign-2026-09-16/super-admin.html `saHdr` (frames
 * 01-30) and the header-parity table in docs/app-map/studio/mocks/super-admin.md.
 *
 * Look only: every control, click target and data-testid is the caller's
 * classic one, passed straight through. Renders only when the caller is inside
 * `isStudio`; classic never mounts this file.
 */
import React from 'react';
import { ArrowLeft, Search, Loader2 } from 'lucide-react';

/** The header's centred search. `spinner` is the tool's own pending marker. */
export function ToolSearch({ value, onChange, placeholder, testid, spinner, spinnerTestid, inHeader = true }) {
  return (
    <span className={inHeader ? 'st31-srch' : 'st31-srch st31-srch-inline'}>
      <Search />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
      />
      {spinner && <Loader2 className="st31-sspin" data-testid={spinnerTestid} />}
    </span>
  );
}

export default function StudioToolHeader({
  icon: Icon,
  title,
  titleExtra,
  sub,
  onBack,
  backLabel = 'Back',
  backTestid,
  search,          // a <ToolSearch/> element, or null
  right,           // the right cluster (cap + buttons), or null
  tabs,            // [{ key, label, testid }] or null
  activeTab,
  onTab,
  subnavRight,
}) {
  return (
    <>
      <div className="st31-hdr">
        <button type="button" className="st-ibtn" onClick={onBack} aria-label={backLabel} title={backLabel} data-testid={backTestid}>
          <ArrowLeft />
        </button>
        {Icon && <span className="st31-glyph"><Icon /></span>}
        <span className="st31-tb">
          <h1 className="st31-ttl"><span className="st31-name">{title}</span>{titleExtra}</h1>
          {sub && <p className="st31-sub">{sub}</p>}
        </span>
        {search || <span className="st31-sp" />}
        {right && <span className="st31-right">{right}</span>}
      </div>
      {tabs && tabs.length > 0 && (
        <div className="st31-subnav">
          <div className="st31-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`st31-tab${activeTab === t.key ? ' st31-on' : ''}`}
                onClick={() => onTab(t.key)}
                data-testid={t.testid}
              >
                {t.label}
              </button>
            ))}
          </div>
          {subnavRight}
        </div>
      )}
    </>
  );
}
